import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const ADMIN_PHONES = ['+447720378051', '+447720378050', '+447415214865'];

const EVENT_LABELS = {
  page_view: 'Page view',
  page_exit: 'Left page',
  click: 'Click',
};

function formatTimestamp(ts) {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString();
}

function formatDuration(seconds) {
  if (seconds == null || seconds === '') return '—';
  const s = Math.round(Number(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec}s`;
}

function formatClickTarget(clickTarget) {
  if (!clickTarget || typeof clickTarget !== 'object') return '—';
  const parts = [];
  if (clickTarget.tagName) parts.push(clickTarget.tagName);
  if (clickTarget.id) parts.push(`#${clickTarget.id}`);
  if (clickTarget.text) parts.push(`"${clickTarget.text.slice(0, 40)}${clickTarget.text.length > 40 ? '…' : ''}"`);
  return parts.length ? parts.join(' ') : '—';
}

function UserLogs() {
  const [logs, setLogs] = useState([]);
  const [invites, setInvites] = useState([]);
  const [filterQuery, setFilterQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const user = getAuth().currentUser;
  const isAdmin = user && ADMIN_PHONES.includes(user.phoneNumber);

  useEffect(() => {
    const fetchInvites = async () => {
      const snap = await getDocs(collection(db, 'invites'));
      setInvites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchInvites();
  }, []);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const ref = collection(db, 'userActivityLogs');
        const q = query(ref, orderBy('timestamp', 'desc'), limit(1000));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setLogs(list);
      } catch (err) {
        console.error('Fetch user logs failed:', err);
        setError(err.message || 'Failed to load logs.');
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  // Map phone -> display name from invites (first matching guest name)
  const phoneToName = invites.reduce((acc, inv) => {
    const phone = inv.phoneNumber;
    if (!phone) return acc;
    if (!acc[phone]) acc[phone] = inv.name || phone;
    return acc;
  }, {});

  // Filter logs by typeahead: match name or phone number (case-insensitive)
  const search = filterQuery.trim().toLowerCase();
  const filteredLogs = search
    ? logs.filter((log) => {
        const name = (phoneToName[log.phoneNumber] || '').toLowerCase();
        const phone = (log.phoneNumber || '').toLowerCase();
        return name.includes(search) || phone.includes(search);
      })
    : logs;

  if (!isAdmin) {
    return <Navigate to="/guest-list" replace />;
  }

  const cellStyle = {
    padding: '10px',
    textAlign: 'left',
    fontFamily: 'var(--font-body)',
    fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
    color: 'var(--text-primary)',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
  };

  return (
    <div className="page-container">
      <h2 className="page-title">User activity logs</h2>
      <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', marginBottom: '16px' }}>
        <Link to="/guest-list" style={{ color: 'var(--link-color)' }}>← Back to Guest List</Link>
      </p>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)', fontWeight: '600', display: 'block', marginBottom: '8px' }}>
          Filter by name or phone number
        </label>
        <input
          type="text"
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          placeholder="Type name or phone number…"
          style={{
            fontFamily: 'var(--font-body)',
            padding: '10px 14px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid rgba(255,255,255,0.3)',
            background: 'var(--card-bg-light)',
            color: 'var(--text-primary)',
            width: '100%',
            maxWidth: '320px',
            boxSizing: 'border-box',
          }}
          aria-label="Filter logs by name or phone number"
        />
        {filterQuery.trim() && (
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '6px' }}>
            Showing {filteredLogs.length} log{filteredLogs.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {error && (
        <p style={{ color: 'var(--button-danger)', marginBottom: '12px' }}>{error}</p>
      )}

      {loading ? (
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>Loading logs…</p>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...cellStyle, fontWeight: '600' }}>Time</th>
                <th style={{ ...cellStyle, fontWeight: '600' }}>Person</th>
                <th style={{ ...cellStyle, fontWeight: '600' }}>Event</th>
                <th style={{ ...cellStyle, fontWeight: '600' }}>Page</th>
                <th style={{ ...cellStyle, fontWeight: '600' }}>Duration</th>
                <th style={{ ...cellStyle, fontWeight: '600' }}>Click / detail</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ ...cellStyle, color: 'var(--text-secondary)' }}>
                    No activity logs yet.
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id}>
                    <td style={cellStyle}>{formatTimestamp(log.timestamp)}</td>
                    <td style={cellStyle}>
                      {phoneToName[log.phoneNumber] ? `${phoneToName[log.phoneNumber]} (${log.phoneNumber})` : (log.phoneNumber || '—')}
                    </td>
                    <td style={cellStyle}>{EVENT_LABELS[log.eventType] || log.eventType}</td>
                    <td style={cellStyle}>{log.pathname || '—'}</td>
                    <td style={cellStyle}>
                      {log.eventType === 'page_exit' ? formatDuration(log.durationSeconds) : '—'}
                    </td>
                    <td style={cellStyle}>
                      {log.eventType === 'click' ? formatClickTarget(log.clickTarget) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default UserLogs;
